import { useCallback, useMemo } from 'react'
import type { useExposeToEndpoint } from '@farcaster/miniapp-host'
import { useWallets } from '@towns/privy'
import { utils } from 'ethers'

export type OxEthProvider = NonNullable<Parameters<typeof useExposeToEndpoint>[0]['ethProvider']>
type ProviderRequestArgs = Parameters<OxEthProvider['request']>[0]

export type MiniappSignatureParams = ProviderRequestArgs['params']

export type MiniappEthTransactionRequest = {
    from?: string
    to?: string
    value?: string
    data?: string
    gas?: string
    gasPrice?: string
    chainId?: string
}

export type MiniappSignatureMethod =
    | 'personal_sign'
    | 'eth_sign'
    | 'eth_signTypedData'
    | 'eth_signTypedData_v4'

export type MiniappTransactionMethod =
    | 'eth_sendTransaction'
    | 'eth_signTransaction'
    | 'eth_sendRawTransaction'

type MiniappProviderOptions = {
    onTransactionRequest?: (args: {
        tx: MiniappEthTransactionRequest
        method: MiniappTransactionMethod
    }) => Promise<void>
    onSignatureRequest?: (args: {
        method: MiniappSignatureMethod
        params: ProviderRequestArgs['params']
        fromAddress: string
    }) => Promise<string>
}

export const useMiniappEthereumProvider = (
    options?: MiniappProviderOptions,
): {
    provider: OxEthProvider
    privyAddress: string | null
    loadProviderForAddress: (address: string) => Promise<OxEthProvider>
} => {
    const { wallets } = useWallets()

    const privyAddress = useMemo(() => {
        const privyWallet = wallets.find(
            (wallet) => wallet.walletClientType === 'privy' && wallet.address,
        )
        return privyWallet?.address ?? null
    }, [wallets])

    const loadProviderForAddress = useCallback(
        async (address: string) => {
            const normalized = address.toLowerCase()
            const wallet = wallets.find((wallet) => wallet.address?.toLowerCase() === normalized)
            if (!wallet || typeof wallet.getEthereumProvider !== 'function') {
                throw new Error(`No wallet available for address ${address}`)
            }
            return (await wallet.getEthereumProvider()) as OxEthProvider
        },
        [wallets],
    )

    const provider = useMemo(() => {
        // This function handles the eth_sendTransaction method.
        // It forces the transaction to be sent from the Privy address.
        const handleEthSendTransaction = async (rpcRequest: ProviderRequestArgs) => {
            if (!privyAddress) {
                throw new Error('Privy address is not available for signature requests')
            }

            const params = Array.isArray(rpcRequest.params) ? (rpcRequest.params ?? []) : []
            const [tx] = params
            if (!tx) {
                throw new Error('eth_sendTransaction missing transaction params')
            }

            console.log('[MiniappPanel:Transaction] 🔍 Incoming eth_sendTransaction request', {
                rpcRequest,
                tx,
            })

            if (options?.onTransactionRequest) {
                await options.onTransactionRequest({
                    tx,
                    method: 'eth_sendTransaction',
                })
            }

            const providerForCall = await loadProviderForAddress(privyAddress)
            return providerForCall.request(rpcRequest)
        }

        // Mirror eth_sendTransaction flow but intercept eth_signTransaction so the modal/validation runs first.
        const handleEthSignTransaction = async (rpcRequest: ProviderRequestArgs) => {
            if (!privyAddress) {
                throw new Error('Privy address is not available for signature requests')
            }

            const params = Array.isArray(rpcRequest.params) ? (rpcRequest.params ?? []) : []
            const [tx] = params
            if (!tx) {
                throw new Error('eth_signTransaction missing transaction params')
            }

            console.log('[MiniappPanel:Transaction] 🔏 Incoming eth_signTransaction request', {
                rpcRequest,
                tx,
            })

            if (options?.onTransactionRequest) {
                await options.onTransactionRequest({
                    tx,
                    method: 'eth_signTransaction',
                })
            }

            const providerForCall = await loadProviderForAddress(privyAddress)
            return providerForCall.request(rpcRequest)
        }

        // Parse raw payloads to display decoded txn details before forwarding to the wallet.
        const handleEthSendRawTransaction = async (rpcRequest: ProviderRequestArgs) => {
            if (!privyAddress) {
                throw new Error('Privy address is not available for signature requests')
            }

            const parsedTx = parseRawTransaction(rpcRequest)

            console.log('[MiniappPanel:Transaction] 📦 Incoming eth_sendRawTransaction request', {
                rpcRequest,
                parsedTx,
            })

            if (options?.onTransactionRequest) {
                await options.onTransactionRequest({
                    tx: parsedTx,
                    method: 'eth_sendRawTransaction',
                })
            }

            const providerForCall = await loadProviderForAddress(privyAddress)
            return providerForCall.request(rpcRequest)
        }

        // This function handles signature requests like personal_sign, eth_sign, etc.
        // It ensures that the signature is requested from the Privy address.
        const handleSignatureRequest = async (rpcRequest: ProviderRequestArgs) => {
            if (!privyAddress) {
                throw new Error('Privy address is not available for signature requests')
            }

            const signerAddress = resolveSignatureAddress(rpcRequest)
            if (!signerAddress) {
                throw new Error('Unable to resolve address for signature request')
            }

            console.log('[MiniappPanel:Signature] 🔍 Incoming signature request', {
                signerAddress,
                privyAddress,
            })

            if (options?.onSignatureRequest) {
                return options.onSignatureRequest?.({
                    method: rpcRequest.method as MiniappSignatureMethod,
                    params: rpcRequest.params,
                    fromAddress: signerAddress,
                })
            }

            const providerForCall = await loadProviderForAddress(privyAddress)
            return providerForCall.request({
                ...rpcRequest,
                params: ensureSignatureAddress(rpcRequest, privyAddress),
            })
        }

        const request = async (rpcRequest: ProviderRequestArgs) => {
            switch (rpcRequest.method) {
                case 'eth_sendTransaction':
                    return handleEthSendTransaction(rpcRequest)
                case 'eth_signTransaction':
                    return handleEthSignTransaction(rpcRequest)
                case 'eth_sendRawTransaction':
                    return handleEthSendRawTransaction(rpcRequest)
                case 'personal_sign':
                case 'eth_sign':
                case 'eth_signTypedData':
                case 'eth_signTypedData_v4':
                    return handleSignatureRequest(rpcRequest)
                default: {
                    const targetAddress = resolveSignatureAddress(rpcRequest) ?? privyAddress
                    if (!targetAddress) {
                        throw new Error('Unable to route RPC request without an address')
                    }
                    const providerForCall = await loadProviderForAddress(targetAddress)
                    return providerForCall.request(rpcRequest)
                }
            }
        }

        const on: OxEthProvider['on'] = async (event, listener) => {
            for (const wallet of wallets) {
                if (wallet.address && typeof wallet.getEthereumProvider === 'function') {
                    const providerInstance = (await wallet.getEthereumProvider()) as OxEthProvider
                    providerInstance.on?.(event, listener)
                }
            }
        }

        const removeListener: OxEthProvider['removeListener'] = async (event, listener) => {
            for (const wallet of wallets) {
                if (wallet.address && typeof wallet.getEthereumProvider === 'function') {
                    const providerInstance = (await wallet.getEthereumProvider()) as OxEthProvider
                    providerInstance.removeListener?.(event, listener)
                }
            }
        }

        return {
            request,
            on,
            removeListener,
        }
    }, [loadProviderForAddress, options, privyAddress, wallets])

    return useMemo(
        () => ({
            provider: provider as OxEthProvider,
            privyAddress,
            loadProviderForAddress,
        }),
        [loadProviderForAddress, privyAddress, provider],
    )
}

const resolveSignatureAddress = (request: ProviderRequestArgs): string | undefined => {
    if (!Array.isArray(request.params)) {
        return undefined
    }
    if (request.method === 'personal_sign') {
        return request.params[1]
    }
    if (request.method === 'eth_sign') {
        return request.params[0]
    }
    if (request.method === 'eth_signTypedData' || request.method === 'eth_signTypedData_v4') {
        return request.params[0]
    }
    if (request.method === 'eth_sendTransaction') {
        const [tx] = request.params
        if (tx && typeof tx === 'object' && 'from' in tx && typeof tx.from === 'string') {
            return tx.from
        }
    }
    return undefined
}

const ensureSignatureAddress = (request: ProviderRequestArgs, address: string): unknown[] => {
    const params = (Array.isArray(request.params) ? [...request.params] : []) as unknown[]
    if (request.method === 'personal_sign') {
        return [params[0], address]
    }
    if (request.method === 'eth_sign') {
        return [address, params[0]]
    }
    if (request.method === 'eth_signTypedData' || request.method === 'eth_signTypedData_v4') {
        return [address, ...params.slice(1)]
    }
    return params
}

// Extracts minimal tx fields from a serialized raw transaction so the approval modal can show context.
const parseRawTransaction = (request: ProviderRequestArgs): MiniappEthTransactionRequest => {
    if (!Array.isArray(request.params)) {
        throw new Error('eth_sendRawTransaction missing params')
    }
    const [rawTx] = request.params
    if (!rawTx || typeof rawTx !== 'string') {
        throw new Error('eth_sendRawTransaction requires serialized payload')
    }

    let parsed
    try {
        parsed = utils.parseTransaction(rawTx)
    } catch (error) {
        throw new Error('Unable to parse raw transaction payload')
    }

    return {
        from: parsed.from ?? undefined,
        to: parsed.to ?? undefined,
        value: parsed.value ? parsed.value.toHexString() : undefined,
        data: parsed.data ?? undefined,
        gas: parsed.gasLimit ? parsed.gasLimit.toHexString() : undefined,
        gasPrice: parsed.gasPrice ? parsed.gasPrice.toHexString() : undefined,
        chainId: typeof parsed.chainId === 'number' ? utils.hexValue(parsed.chainId) : undefined,
    }
}
